import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import useEvent from "react-use-event-hook";
import { useSetRecoilState } from "recoil";

import { IMessagesTypeEnum } from "@ryde/controls";
import { useYupValidationResolver } from "@ryde/redux";
import {
  errors,
  getAccessToken,
  Log,
  RecaptchaEnterpriseActions,
  setAccessToken,
} from "@ryde/shared";
import {
  apiDataFetchingLandingAtom,
  ClalLoginStepFormFieldsEnum,
  createActiveStepError,
  createMessage,
  isLoggedInLandingAtom,
  IVerifyCodeLandingModel,
  landingMessagesAtom,
  LandingStepEnum,
  LandingStepErrorsEnum,
  useLandingActions,
  useLandingApiAuth,
  useLandingOtpErrors,
  useLandingRecaptchaToken,
  VerifyCodeStepFormFieldsEnum,
} from "@ryde/utils-landings";

import { yupVerifyCodeLandingValidationSchema } from "./parts";

export const useStepVerifyCodeClal = () => {
  const [searchParams] = useSearchParams();
  const { goToStep, setActiveStepErrors } = useLandingActions();
  const setIsLoggedIn = useSetRecoilState(isLoggedInLandingAtom);
  const { loginPublicOtp, requestPublicOtp } = useLandingApiAuth();
  const { handleErrors } = useLandingOtpErrors();
  const { getRecaptchaToken } = useLandingRecaptchaToken();
  const setServerErrors = useSetRecoilState(landingMessagesAtom);
  const setApiLoading = useSetRecoilState(apiDataFetchingLandingAtom);
  const { t } = useTranslation();

  const resolver = useYupValidationResolver(
    yupVerifyCodeLandingValidationSchema,
    "landing",
  );

  const methods = useForm<IVerifyCodeLandingModel>({
    mode: "all",
    reValidateMode: "onChange",
    criteriaMode: "firstError",
    defaultValues: {
      verificationCode: "",
    },
    resolver,
  });

  const {
    clearErrors,
    getValues,
    trigger,
    watch,
    formState: { isValid },
  } = methods;

  const email = searchParams.get(ClalLoginStepFormFieldsEnum.Email);
  const verificationCodeValue = watch(
    VerifyCodeStepFormFieldsEnum.VerificationCode,
  );

  const isContinueCanBeApply = useMemo(() => {
    return isValid && verificationCodeValue;
  }, [isValid, verificationCodeValue]);

  const handleCodeVerification = useEvent(async (): Promise<void> => {
    const isValidAfterTrigger = await trigger();

    if (!isValidAfterTrigger || !email) {
      return;
    }

    setApiLoading(true);
    setServerErrors(createMessage([]));
    const verificationCode = getValues(
      VerifyCodeStepFormFieldsEnum.VerificationCode,
    );
    const recaptchaToken = await getRecaptchaToken(
      RecaptchaEnterpriseActions.LOGIN,
    );

    try {
      const verifyOtpResponse = await loginPublicOtp({
        verificationCode,
        email,
        recaptchaToken,
      });

      const { success, error, data } = verifyOtpResponse ?? {};

      if (success && !error && data?.access_token) {
        Log.debug("Auth token received for email: ", email);
        setAccessToken(data?.access_token);

        if (getAccessToken()) {
          setIsLoggedIn(true);
          goToStep(LandingStepEnum.ChooseStationStep);
        }

        setServerErrors(createMessage([]));
        goToStep(LandingStepEnum.ChooseStationStep);
      } else {
        handleErrors({
          error,
          defaultAction(e) {
            setServerErrors(createMessage([t("errors.errorOtpIncorrect")]));
          },
        });
      }
    } catch (error) {
      Log.warn("Error while verifying code", error);
      setActiveStepErrors(
        createActiveStepError(LandingStepErrorsEnum.GeneralError),
      );
      goToStep(LandingStepEnum.ErrorStep);
    } finally {
      setApiLoading(false);
    }
  });

  const resendOtp = useEvent(async (): Promise<void> => {
    setApiLoading(true);
    setServerErrors(createMessage([]));
    const recaptchaToken = await getRecaptchaToken(
      RecaptchaEnterpriseActions.REQUEST_OTP,
    );

    if (!email) {
      setServerErrors(createMessage([errors.auth.errorWhileRequestOTP]));
      setApiLoading(false);
      return;
    }

    try {
      const requestOtpResponse = await requestPublicOtp({
        recaptchaToken,
        email,
      });

      const { success, error } = requestOtpResponse ?? {};

      if (success) {
        setServerErrors(
          createMessage(
            [t("landing.resendOtpSuccess")],
            IMessagesTypeEnum.SUCCESS,
          ),
        );
        clearErrors(VerifyCodeStepFormFieldsEnum.VerificationCode);
      } else {
        handleErrors({
          error,
          defaultAction(e) {
            setServerErrors(createMessage([errors.auth.errorWhileRequestOTP]));
          },
        });
      }
    } catch (error) {
      Log.warn("Error while requesting OTP", error);
      setActiveStepErrors(
        createActiveStepError(LandingStepErrorsEnum.GeneralError),
      );
      goToStep(LandingStepEnum.ErrorStep);
    } finally {
      setApiLoading(false);
    }
  });

  return { methods, handleCodeVerification, resendOtp, isContinueCanBeApply };
};
